import { Icon } from '@iconify/react';
import { SportMarkTile } from '../components/ui/SportMarks';

/**
 * Dev-only comparison page (reachable at /dev/icons, no auth) for choosing the
 * sport icon family. Renders every candidate set on the real gradient tiles used
 * on the hub so the marks can be judged in context. Icons here are fetched from the
 * Iconify API at runtime; once a set is chosen we install its `@iconify-json/*`
 * package and bundle the four marks offline for the native app.
 */

type SportKey = 'football' | 'cycling' | 'basketball' | 'tennis';

const SPORTS: { key: SportKey; label: string }[] = [
  { key: 'football', label: 'Football' },
  { key: 'cycling', label: 'Cycling' },
  { key: 'basketball', label: 'Basketball' },
  { key: 'tennis', label: 'Tennis' }
];

// Gradient per sport — mirrors SportMarkTile so the comparison is true to the hub.
const TILE: Record<SportKey, { bg: string; shadow: string }> = {
  football: {
    bg: 'linear-gradient(145deg,#34E88E 0%,#1BCB7A 42%,#08A079 100%)',
    shadow: '0 8px 16px -7px rgba(36,197,106,0.55)'
  },
  cycling: {
    bg: 'linear-gradient(145deg,#FF9F4A 0%,#F07A2E 48%,#D45F12 100%)',
    shadow: '0 8px 16px -7px rgba(200,90,20,0.45)'
  },
  basketball: {
    bg: 'linear-gradient(145deg,#FF8A5C 0%,#E85D32 52%,#C2410C 100%)',
    shadow: '0 6px 14px -7px rgba(200,70,20,0.35)'
  },
  tennis: {
    bg: 'linear-gradient(145deg,#B8F07A 0%,#7AD04A 52%,#4FA828 100%)',
    shadow: '0 6px 14px -7px rgba(80,160,40,0.35)'
  }
};

type IconSet = {
  id: string;
  name: string;
  license: string;
  /**
   * 'mono'  — single-colour glyph rendered white on the sport's gradient tile.
   * 'color' — full-colour emoji rendered on a neutral light tile (keeps its colours).
   */
  variant?: 'mono' | 'color';
  /** Null where the set has no good mark for that sport. */
  icons: Partial<Record<SportKey, string>>;
};

// Every name below was confirmed against the Iconify search API.
const SETS: IconSet[] = [
  {
    id: 'fluent-emoji-flat',
    name: 'Fluent Emoji · Flat (full colour)',
    license: 'MIT',
    variant: 'color',
    icons: {
      football: 'fluent-emoji-flat:soccer-ball',
      cycling: 'fluent-emoji-flat:bicycle',
      basketball: 'fluent-emoji-flat:basketball',
      tennis: 'fluent-emoji-flat:tennis'
    }
  },
  {
    id: 'fluent-emoji-high-contrast',
    name: 'Fluent Emoji · High Contrast',
    license: 'MIT',
    icons: {
      football: 'fluent-emoji-high-contrast:soccer-ball',
      cycling: 'fluent-emoji-high-contrast:bicycle',
      basketball: 'fluent-emoji-high-contrast:basketball',
      tennis: 'fluent-emoji-high-contrast:tennis'
    }
  },
  {
    id: 'twemoji',
    name: 'Twemoji · Twitter (full colour)',
    license: 'CC BY 4.0',
    variant: 'color',
    icons: {
      football: 'twemoji:soccer-ball',
      cycling: 'twemoji:bicycle',
      basketball: 'twemoji:basketball',
      tennis: 'twemoji:tennis'
    }
  },
  {
    id: 'noto',
    name: 'Noto Emoji · Google (full colour)',
    license: 'Apache 2.0',
    variant: 'color',
    icons: {
      football: 'noto:soccer-ball',
      cycling: 'noto:bicycle',
      basketball: 'noto:basketball',
      tennis: 'noto:tennis'
    }
  },
  {
    id: 'openmoji',
    name: 'OpenMoji (full colour)',
    license: 'CC BY-SA 4.0',
    variant: 'color',
    icons: {
      football: 'openmoji:soccer-ball',
      cycling: 'openmoji:bicycle',
      basketball: 'openmoji:basketball',
      tennis: 'openmoji:tennis'
    }
  },
  {
    id: 'emojione',
    name: 'EmojiOne (full colour)',
    license: 'CC BY 4.0',
    variant: 'color',
    icons: {
      football: 'emojione:soccer-ball',
      cycling: 'emojione:bicycle',
      basketball: 'emojione:basketball',
      tennis: 'emojione:tennis'
    }
  },
  {
    id: 'solar-bold',
    name: 'Solar · Bold',
    license: 'CC BY 4.0',
    icons: {
      football: 'solar:football-bold',
      cycling: 'solar:bicycling-bold',
      basketball: 'solar:basketball-bold',
      tennis: 'solar:tennis-bold'
    }
  },
  {
    id: 'solar-bold-fluent-ball',
    name: 'Solar Bold + Fluent football (mix)',
    license: 'CC BY 4.0 / MIT',
    icons: {
      // The one you liked, with the “flower” football swapped for the Fluent emoji ball.
      football: 'fluent-emoji-high-contrast:soccer-ball',
      cycling: 'solar:bicycling-bold',
      basketball: 'solar:basketball-bold',
      tennis: 'solar:tennis-bold'
    }
  },
  {
    id: 'solar-bold-duotone',
    name: 'Solar · Bold Duotone',
    license: 'CC BY 4.0',
    icons: {
      football: 'solar:football-bold-duotone',
      cycling: 'solar:bicycling-bold-duotone',
      basketball: 'solar:basketball-bold-duotone',
      tennis: 'solar:tennis-bold-duotone'
    }
  },
  {
    id: 'phosphor-fill',
    name: 'Phosphor · Fill',
    license: 'MIT',
    icons: {
      football: 'ph:soccer-ball-fill',
      cycling: 'ph:person-simple-bike-fill',
      basketball: 'ph:basketball-fill',
      tennis: 'ph:tennis-ball-fill'
    }
  },
  {
    id: 'phosphor-duotone',
    name: 'Phosphor · Duotone',
    license: 'MIT',
    icons: {
      football: 'ph:soccer-ball-duotone',
      cycling: 'ph:person-simple-bike-duotone',
      basketball: 'ph:basketball-duotone',
      tennis: 'ph:tennis-ball-duotone'
    }
  },
  {
    id: 'material',
    name: 'Material Symbols · Rounded',
    license: 'Apache 2.0',
    icons: {
      football: 'material-symbols:sports-soccer-rounded',
      cycling: 'material-symbols:directions-bike-rounded',
      basketball: 'material-symbols:sports-basketball-rounded',
      tennis: 'material-symbols:sports-tennis-rounded'
    }
  },
  {
    id: 'hugeicons',
    name: 'Hugeicons · Stroke',
    license: 'MIT',
    icons: {
      football: 'hugeicons:football',
      cycling: 'hugeicons:bicycle',
      basketball: 'hugeicons:basketball-01',
      tennis: 'hugeicons:tennis-ball'
    }
  },
  {
    id: 'tabler',
    name: 'Tabler',
    license: 'MIT',
    icons: {
      football: 'tabler:ball-football',
      cycling: 'tabler:bike-filled',
      basketball: 'tabler:ball-basketball',
      tennis: 'tabler:ball-tennis'
    }
  },
  {
    id: 'mingcute',
    name: 'MingCute · Fill',
    license: 'Apache 2.0',
    icons: {
      football: 'mingcute:football-fill',
      cycling: 'mingcute:bike-fill',
      basketball: 'mingcute:basketball-fill'
      // no tennis mark in MingCute
    }
  },
  {
    id: 'fluent',
    name: 'Fluent · Filled',
    license: 'MIT',
    icons: {
      football: 'fluent:sport-soccer-24-filled',
      cycling: 'fluent:vehicle-bicycle-24-filled',
      basketball: 'fluent:sport-basketball-24-filled'
      // no tennis mark in Fluent
    }
  },
  {
    id: 'game-icons',
    name: 'Game Icons · Detailed (mono)',
    license: 'CC BY 3.0',
    icons: {
      football: 'game-icons:soccer-ball',
      cycling: 'game-icons:cycling',
      basketball: 'game-icons:basketball-ball',
      tennis: 'game-icons:tennis-ball'
    }
  }
];

// Noto has no bike-helmet emoji, but it has dynamic "in motion" cycling marks
// that read far better than the static bicycle. Compare them side by side.
const NOTO_CYCLING: { icon: string; label: string }[] = [
  { icon: 'noto:bicycle', label: 'Bicycle (default)' },
  { icon: 'noto:person-biking', label: 'Cyclist · road' },
  { icon: 'noto:person-mountain-biking', label: 'Cyclist · MTB' }
];

function ColorTile({ icon, label }: { icon: string; label: string }) {
  return (
    <div className="flex flex-col items-center gap-2">
      <div
        className="relative grid h-[46px] w-[46px] place-items-center overflow-hidden rounded-[14px]"
        style={{ background: '#F1F1F5', boxShadow: 'inset 0 0 0 1px rgba(20,20,50,0.08)' }}
      >
        <Icon icon={icon} width={30} height={30} />
      </div>
      <span className="text-[10px] font-semibold text-[#9A9AA4]">{label}</span>
    </div>
  );
}

function IconTile({
  sport,
  icon,
  variant = 'mono'
}: {
  sport: SportKey;
  icon: string | undefined;
  variant?: 'mono' | 'color';
}) {
  const tile = TILE[sport];
  const isColor = variant === 'color';
  return (
    <div className="flex flex-col items-center gap-2">
      <div
        className="relative grid h-[46px] w-[46px] place-items-center overflow-hidden rounded-[14px]"
        style={
          isColor
            ? { background: '#F1F1F5', boxShadow: 'inset 0 0 0 1px rgba(20,20,50,0.08)' }
            : { background: tile.bg, boxShadow: tile.shadow }
        }
      >
        {!isColor && (
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.22]"
            style={{ background: 'radial-gradient(circle at 28% 18%, #fff 0%, transparent 58%)' }}
          />
        )}
        {icon ? (
          isColor ? (
            <Icon icon={icon} width={30} height={30} />
          ) : (
            <Icon icon={icon} width={28} height={28} color="#ffffff" />
          )
        ) : (
          <span className={`text-[18px] font-bold ${isColor ? 'text-black/25' : 'text-white/45'}`}>—</span>
        )}
      </div>
      <span className="text-[10px] font-semibold text-[#9A9AA4]">{sport}</span>
    </div>
  );
}

function SetRow({ set }: { set: IconSet }) {
  return (
    <section className="rounded-[20px] border border-black/[0.07] bg-white p-5 shadow-[0_10px_26px_-22px_rgba(20,20,50,0.4)]">
      <div className="mb-4 flex items-baseline justify-between gap-3">
        <h2 className="text-[16px] font-extrabold tracking-tight text-ink">{set.name}</h2>
        <span className="text-[11px] font-semibold text-[#9A9AA4]">{set.license}</span>
      </div>
      <div className="flex flex-wrap gap-6">
        {SPORTS.map((s) => (
          <IconTile key={s.key} sport={s.key} icon={set.icons[s.key]} variant={set.variant} />
        ))}
      </div>
    </section>
  );
}

export default function SportIconShowcase() {
  return (
    <div className="native-scroll h-full w-full overflow-y-auto bg-[#FBFBFD] px-5 py-10 text-ink">
      <div className="mx-auto flex w-full max-w-[680px] flex-col gap-6">
        <header>
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-accent-ink">
            Icon comparison
          </p>
          <h1 className="mt-1 text-[30px] font-extrabold tracking-title text-ink">Sport marks</h1>
          <p className="mt-2 max-w-[520px] text-[14px] leading-[1.5] text-ink2">
            Each set rendered on the real hub gradient tiles. Pick the family you like — I&apos;ll
            then bundle just those four marks offline for the native app. Icons here load from the
            Iconify API (online only); the shipped version won&apos;t.
          </p>
        </header>

        {/* Chosen family: Noto. Pick the cycling mark (no bike-helmet emoji exists in Noto). */}
        <section className="rounded-[20px] border border-accent/30 bg-accent/[0.06] p-5">
          <div className="mb-1 flex items-baseline justify-between gap-3">
            <h2 className="text-[16px] font-extrabold tracking-tight text-ink">
              Noto · choose the cycling mark
            </h2>
            <span className="text-[11px] font-semibold text-accent-ink">chosen family</span>
          </div>
          <p className="mb-4 text-[12px] leading-[1.5] text-ink2">
            No bike-helmet emoji in Noto — but the rider-in-motion marks beat the static bike. Tell
            me which one.
          </p>
          <div className="flex flex-wrap gap-6">
            {NOTO_CYCLING.map((c) => (
              <ColorTile key={c.icon} icon={c.icon} label={c.label} />
            ))}
          </div>
        </section>

        {/* Shipped marks (Noto), for reference */}
        <section className="rounded-[20px] border border-dashed border-black/15 bg-wash p-5">
          <div className="mb-4 flex items-baseline justify-between gap-3">
            <h2 className="text-[16px] font-extrabold tracking-tight text-ink">Shipped (Noto)</h2>
            <span className="text-[11px] font-semibold text-[#9A9AA4]">in app now</span>
          </div>
          <div className="flex flex-wrap gap-6">
            <div className="flex flex-col items-center gap-2">
              <SportMarkTile sport="football" />
              <span className="text-[10px] font-semibold text-[#9A9AA4]">football</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <SportMarkTile sport="cycling" />
              <span className="text-[10px] font-semibold text-[#9A9AA4]">cycling</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <SportMarkTile sport="basketball" />
              <span className="text-[10px] font-semibold text-[#9A9AA4]">basketball</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <SportMarkTile sport="tennis" />
              <span className="text-[10px] font-semibold text-[#9A9AA4]">tennis</span>
            </div>
          </div>
        </section>

        {SETS.map((set) => (
          <SetRow key={set.id} set={set} />
        ))}

        <p className="pb-6 pt-2 text-center text-[12px] font-medium text-ink3">
          Tell me the set id you want (e.g. “solar-bold-duotone”) and I&apos;ll wire it in.
        </p>
      </div>
    </div>
  );
}
